import { useOktaAuth } from "@okta/okta-react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useDocApiClient } from "../../api/apiClients";
import { config } from "../../config";
import {
  AssetAttrs,
  AssetAttrsDetails,
  PaginatedData,
  PaginationParams,
  PipelineConfigAttrs,
  ProcessingAttrsDetails,
} from "../../interfaces/api";
import { ExportFormat, ObjectId } from "../../interfaces/common";
import {
  AssetAddDocumentRequest,
  AssetDeleteDocumentRequest,
  ClassificationOutput,
  CreateAssetRequest,
  CreateAssetResponse,
  ExtractionOutput,
  PipelineConfig,
  PipelineConfigDb,
  ProcessingRequest,
  ProcessingResponse,
} from "../../interfaces/doc";
import { PipelineConfigSchemaType } from "../../schemas/OrionConfig";
import { useNotifications } from "../useNotifications/useNotifications";

const useDocument = (documentId: ObjectId | undefined) => {
  const apiClient = useDocApiClient<{ url: string }>();

  return useQuery({
    queryKey: ["document", documentId],
    queryFn: () =>
      documentId ? apiClient("GET", `/document/${documentId}`) : undefined,
    placeholderData: keepPreviousData,
    staleTime: 600000,
    enabled: !!documentId,
  });
};

const useAsset = (assetId: ObjectId) => {
  const apiClient = useDocApiClient<AssetAttrsDetails>();

  return useQuery({
    queryKey: ["asset", assetId],
    queryFn: () => apiClient("GET", `/front/asset/${assetId}`),
    placeholderData: keepPreviousData,
  });
};

const useAssets = (params: PaginationParams = {}) => {
  const { page, limit, sortFields } = params;

  const apiClient = useDocApiClient<PaginatedData<AssetAttrs>>();

  return useQuery({
    queryKey: ["assets", page, limit, sortFields],
    queryFn: () =>
      apiClient("GET", `/front/asset`, null, {
        page,
        limit,
        sort_fields: sortFields ? JSON.stringify(sortFields) : undefined,
      }),
    placeholderData: keepPreviousData,
    refetchInterval: 10000,
  });
};

const useCreateAsset = () => {
  const apiClient = useDocApiClient<CreateAssetResponse>();
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  const createAsset = async (
    files: File[],
    config_id: ObjectId,
    workspace_id: string,
    name?: string
  ) => {
    const request: CreateAssetRequest = {
      name,
      documents: files.map((f) => f.name),
      config_id,
      workspace_id,
    };
    const response = await apiClient("POST", `/asset`, request);

    if (response.pre_signed_urls) {
      await Promise.all(
        response.pre_signed_urls.map(async ({ url, fields }, index) => {
          const formData = new FormData();

          Object.entries(fields).forEach(([key, value]) => {
            formData.append(key, value);
          });

          formData.append("file", files[index]);

          return fetch(url, {
            method: "POST",
            body: formData,
          });
        })
      );
    }
    return response;
  };

  return useMutation({
    mutationFn: async ({
      files,
      workspace_id,
      name,
      config_id,
    }: {
      files: File[];
      workspace_id: string;
      name?: string;
      config_id: ObjectId;
    }) => {
      const data = await createAsset(files, config_id, workspace_id, name);
      return data;
    },
    onSuccess: async () => {
      notifications.show("Dossier créer avec succès", {
        severity: "success",
        autoHideDuration: 3000,
      });
    },
    onError: async () => {
      notifications.show(
        "Impossible de créer le dossier, une erreur est survenue",
        {
          severity: "error",
          autoHideDuration: 3000,
        }
      );
    },
    onSettled: async () => {
      queryClient.refetchQueries({ queryKey: ["assets"] });
    },
  });
};

const useDeleteAsset = () => {
  const apiClient = useDocApiClient<void>();
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  return useMutation({
    mutationFn: async (assetId: string) => {
      return await apiClient("DELETE", `/asset/${assetId}`);
    },
    onMutate: async (assetId: string) => {
      await queryClient.cancelQueries({ queryKey: ["asset", assetId] });
      await queryClient.cancelQueries({ queryKey: ["assets"] });

      const previousAssets = queryClient.getQueryData([
        "assets",
      ]) as AssetAttrsDetails[];

      queryClient.setQueryData(
        ["assets"],
        (oldAssets: AssetAttrsDetails[] | undefined) => {
          return oldAssets
            ? oldAssets.filter((asset) => asset._id !== assetId)
            : oldAssets;
        }
      );
      return { previousAssets };
    },
    onError: (err, assetId, context) => {
      notifications.show(
        "Impossible de supprimer le dossier, une erreur est survenue",
        {
          severity: "error",
          autoHideDuration: 3000,
        }
      );
      queryClient.setQueryData(["assets"], context?.previousAssets);
    },
    onSettled: (data, err, assetId, context) => {
      queryClient.invalidateQueries({ queryKey: ["asset", assetId] });
      queryClient.invalidateQueries({ queryKey: ["assets"] });
    },
  });
};

const useAddDocumentsAsset = () => {
  const apiClient = useDocApiClient<CreateAssetResponse>();
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  const addDocumentsAsset = async (assetId: ObjectId, files: File[]) => {
    const request: AssetAddDocumentRequest = {
      documents: files.map((f) => f.name),
    };
    const response = await apiClient(
      "POST",
      `/asset/${assetId}/documents/add`,
      request
    );
    if (response.pre_signed_urls) {
      await Promise.all(
        response.pre_signed_urls.map(async ({ url, fields }, index) => {
          const formData = new FormData();

          Object.entries(fields).forEach(([key, value]) => {
            formData.append(key, value);
          });

          formData.append("file", files[index]);

          return fetch(url, {
            method: "POST",
            body: formData,
          });
        })
      );
    }
    return response;
  };

  return useMutation({
    mutationFn: async ({
      assetId,
      files,
    }: {
      assetId: string;
      files: File[];
    }) => {
      await addDocumentsAsset(assetId, files);
    },
    onSuccess: async () => {
      notifications.show(
        "Les documents ont été ajoutés au dossier avec succès",
        {
          severity: "success",
          autoHideDuration: 3000,
        }
      );
    },
    onError: async () => {
      notifications.show(
        "Impossible d'ajouter les documents au dossier, une erreur est survenue",
        {
          severity: "error",
          autoHideDuration: 3000,
        }
      );
    },
    onSettled: async (data, err, variables, context) => {
      queryClient.refetchQueries({ queryKey: ["asset", variables.assetId] });
      queryClient.refetchQueries({ queryKey: ["assets"] });
    },
  });
};

const useDeleteDocumentsAsset = () => {
  const apiClient = useDocApiClient<void>();
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  const deleteDocumentsAsset = async (
    assetId: ObjectId,
    documents: ObjectId[]
  ) => {
    const request: AssetDeleteDocumentRequest = {
      documents: documents,
    };
    await apiClient("POST", `/asset/${assetId}/documents/delete`, request);
  };

  return useMutation({
    mutationFn: async ({
      assetId,
      documents,
    }: {
      assetId: string;
      documents: ObjectId[];
    }) => {
      await deleteDocumentsAsset(assetId, documents);
    },
    onSuccess: async () => {
      notifications.show(
        "Les documents ont été supprimés au dossier avec succès",
        {
          severity: "success",
          autoHideDuration: 3000,
        }
      );
    },
    onError: async () => {
      notifications.show(
        "Impossible de supprimer les documents du dossier, une erreur est survenue",
        {
          severity: "error",
          autoHideDuration: 3000,
        }
      );
    },
    onSettled: async (data, err, variables, context) => {
      queryClient.refetchQueries({ queryKey: ["asset", variables.assetId] });
      queryClient.refetchQueries({ queryKey: ["assets"] });
    },
  });
};

const useConfig = (configId: ObjectId) => {
  const apiClient = useDocApiClient<PipelineConfigDb>();

  return useQuery({
    queryKey: ["config", configId],
    queryFn: () => apiClient("GET", `/config/${configId}`),
    placeholderData: keepPreviousData,
  });
};

const useConfigs = () => {
  const apiClient = useDocApiClient<PipelineConfigAttrs[]>();

  return useQuery({
    queryKey: ["configs"],
    queryFn: async () => {
      return await apiClient("GET", `/front/config`);
    },
    placeholderData: keepPreviousData,
  });
};

const useCreateConfig = () => {
  const apiClient = useDocApiClient<PipelineConfigDb>();
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  return useMutation({
    mutationFn: async ({
      name,
      workspace_id,
      config,
    }: {
      name: string;
      workspace_id: string | null;
      config: PipelineConfigSchemaType;
    }) => {
      const request = {
        name,
        workspace_id,
        config,
      };
      return await apiClient("POST", `/config`, request);
    },
    onSuccess: async () => {
      notifications.show("La configuration a été créée avec succès", {
        severity: "success",
        autoHideDuration: 3000,
      });
    },
    onError: async () => {
      notifications.show(
        "Impossible de créer la configuration, une erreur est survenue",
        {
          severity: "error",
          autoHideDuration: 3000,
        }
      );
    },
    onSettled: (data, err, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["configs"] });
    },
  });
};

const useDuplicateConfig = () => {
  const apiClient = useDocApiClient<PipelineConfigDb>();
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  return useMutation({
    mutationFn: async (configId: ObjectId) => {
      return await apiClient("POST", `/config/${configId}/duplicate`);
    },
    onSuccess: async () => {
      notifications.show("La configuration a été dupliquée avec succès", {
        severity: "success",
        autoHideDuration: 3000,
      });
    },
    onError: async () => {
      notifications.show(
        "Impossible de dupliquer la configuration, une erreur est survenue",
        {
          severity: "error",
          autoHideDuration: 3000,
        }
      );
    },
    onSettled: (data, err, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["configs"] });
    },
  });
};

const useDeleteConfig = () => {
  const apiClient = useDocApiClient<void>();
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  return useMutation({
    mutationFn: async (configId: string) => {
      return await apiClient("DELETE", `/config/${configId}`);
    },
    onMutate: async (configId: string) => {
      await queryClient.cancelQueries({ queryKey: ["config", configId] });
      await queryClient.cancelQueries({ queryKey: ["configs"] });

      const previousConfigs = queryClient.getQueryData([
        "configs",
      ]) as PipelineConfigDb[];

      queryClient.setQueryData(
        ["configs"],
        (oldConfigs: PipelineConfigAttrs[] | undefined) => {
          return oldConfigs
            ? oldConfigs.filter((config) => config._id !== configId)
            : oldConfigs;
        }
      );
      return { previousConfigs };
    },
    onSuccess: async () => {
      notifications.show("La configuration a été supprimée avec succès", {
        severity: "success",
        autoHideDuration: 3000,
      });
    },
    onError: (err, configId, context) => {
      notifications.show(
        "Impossible de supprimer la configuration, une erreur est survenue",
        {
          severity: "error",
          autoHideDuration: 3000,
        }
      );
      queryClient.setQueryData(["configs"], context?.previousConfigs);
    },
    onSettled: (data, err, configId, context) => {
      queryClient.invalidateQueries({ queryKey: ["config", configId] });
      queryClient.invalidateQueries({ queryKey: ["configs"] });
    },
  });
};

const useUpdateConfig = () => {
  const apiClient = useDocApiClient<void>();
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  return useMutation({
    mutationFn: async ({
      configId,
      name,
      config,
    }: {
      configId: ObjectId;
      name: string;
      config: PipelineConfigSchemaType;
    }) => {
      const request = {
        name: name,
        config: config,
      };
      await apiClient("PUT", `/config/${configId}`, request);
    },
    onMutate: async ({
      configId,
      config,
    }: {
      configId: ObjectId;
      config: PipelineConfigSchemaType;
    }) => {
      await queryClient.cancelQueries({ queryKey: ["config", configId] });
      await queryClient.cancelQueries({ queryKey: ["configs"] });

      const previousConfig = queryClient.getQueryData(["config", configId]);
      const previousConfigs = queryClient.getQueryData(["configs"]);

      queryClient.setQueryData(
        ["config", configId],
        (oldConfig: PipelineConfigDb | undefined) => {
          return oldConfig
            ? {
                ...oldConfig,
                config: config,
                updated_date: new Date(),
              }
            : oldConfig;
        }
      );
      queryClient.setQueryData(
        ["configs"],
        (oldConfigs: PipelineConfigAttrs[] | undefined) => {
          return oldConfigs
            ? oldConfigs.map((oldconfig) =>
                oldconfig._id === configId
                  ? {
                      ...oldconfig,
                      config: config,
                      updated_date: new Date(),
                    }
                  : oldconfig
              )
            : oldConfigs;
        }
      );

      return { previousConfig, previousConfigs };
    },
    onSuccess: async () => {
      notifications.show("La configuration a été éditée avec succès", {
        severity: "success",
        autoHideDuration: 3000,
      });
    },
    onError: (err, variables, context) => {
      notifications.show(
        "Impossible d'éditer la configuration, une erreur est survenue",
        {
          severity: "error",
          autoHideDuration: 3000,
        }
      );
      queryClient.setQueryData(
        ["config", variables.configId],
        context?.previousConfig
      );
      queryClient.setQueryData(["configs"], context?.previousConfigs);
    },
    onSettled: async (data, err, variables, context) => {
      queryClient.refetchQueries({ queryKey: ["config", variables.configId] });
      queryClient.refetchQueries({ queryKey: ["configs"] });
    },
  });
};

const useProcessing = (processingId: ObjectId) => {
  const apiClient = useDocApiClient<ProcessingAttrsDetails>();

  return useQuery({
    queryKey: ["processing", processingId],
    queryFn: () => apiClient("GET", `/front/processing/${processingId}`),
    placeholderData: keepPreviousData,
  });
};

const useExportProcessings = () => {
  const notifications = useNotifications();
  const { authState } = useOktaAuth();

  const exportMutation = async ({
    assetIds,
    format,
  }: {
    assetIds: ObjectId[];
    format?: ExportFormat;
  }) => {
    const params = new URLSearchParams();
    const authorization = `Bearer ${authState?.accessToken?.accessToken}`;

    if (format) params.append("format", format);
    assetIds.forEach((id) => params.append("ids", id));

    const response = await fetch(
      `${config.REACT_APP_DOC_BACKEND_URL}/processing/export?${params.toString()}`,
      {
        method: "GET",
        headers: {
          Authorization: authorization,
          Accept: "application/octet-stream",
        },
      }
    );

    if (!response.ok) {
      throw new Error(
        `Export failed: ${response.status} ${response.statusText}`
      );
    }

    return response.blob();
  };

  return useMutation({
    mutationFn: async ({
      assetIds,
      format,
    }: {
      assetIds: ObjectId[];
      format?: ExportFormat;
    }) => {
      return await exportMutation({ assetIds, format });
    },
    onSuccess: (blob, variables) => {
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download =
        variables.format !== "csv_for_excel"
          ? `export.${variables.format}`
          : "export.csv";
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    onError: async () => {
      notifications.show(
        "Impossible d'exporter le processing, une erreur est survenue",
        {
          severity: "error",
          autoHideDuration: 3000,
        }
      );
    },
  });
};

const useTriggerProcessing = () => {
  const apiClient = useDocApiClient<ProcessingResponse>();
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  const createProcessing = async (
    assetId?: ObjectId,
    configId?: ObjectId,
    config?: PipelineConfig,
    files?: { name: string; file: File }[]
  ) => {
    const request: ProcessingRequest = {
      config_id: configId,
      asset_id: assetId,
      documents: files?.map((f) => f.name),
      config: config,
    };
    const response = await apiClient("POST", `/processing`, request);

    if (files && response.pre_signed_urls) {
      await Promise.all(
        response.pre_signed_urls.map(async ({ url, fields }, index) => {
          const formData = new FormData();

          Object.entries(fields).forEach(([key, value]) => {
            formData.append(key, value);
          });

          formData.append("file", files[index].file);

          return fetch(url, {
            method: "POST",
            body: formData,
          });
        })
      );
    }
    return response;
  };

  return useMutation({
    mutationFn: async ({
      assetId,
      configId,
      config,
      files,
    }: {
      assetId?: ObjectId;
      configId?: ObjectId;
      config?: PipelineConfig;
      files?: { name: string; file: File }[];
    }) => {
      await createProcessing(assetId, configId, config, files);
    },
    onSuccess: async () => {
      notifications.show("Un processing a été déclenché avec succès", {
        severity: "success",
        autoHideDuration: 3000,
      });
    },
    onError: async () => {
      notifications.show(
        "Impossible de déclencher le processing, une erreur est survenue",
        {
          severity: "error",
          autoHideDuration: 3000,
        }
      );
    },
    onSettled: async (data, err, variables, context) => {
      variables.assetId &&
        queryClient.refetchQueries({ queryKey: ["asset", variables.assetId] });
      queryClient.refetchQueries({ queryKey: ["assets"] });
    },
  });
};

const useUpdateReview = () => {
  const apiClient = useDocApiClient<void>();
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  return useMutation({
    mutationFn: ({
      processingId,
      review,
      coreIndex,
    }: {
      processingId: ObjectId;
      review: {
        classification: ClassificationOutput | null;
        extraction: ExtractionOutput | null;
      };
      coreIndex: number;
    }) => {
      return apiClient(
        "PUT",
        `/processing/${processingId}/review/${coreIndex}`,
        review
      );
    },
    onError: () => {
      notifications.show("Une erreur est survenue", {
        severity: "error",
        autoHideDuration: 3000,
      });
    },
    onSettled: (data, err, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: ["processing", variables.processingId],
      });
    },
  });
};

const useUpdateWorkflow = () => {
  const apiClient = useDocApiClient<void>();
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  return useMutation({
    mutationFn: ({
      processingId,
      assetId,
      workflow,
    }: {
      processingId: ObjectId;
      assetId: ObjectId;
      workflow: string;
    }) =>
      apiClient(
        "PUT",
        `/processing/${processingId}/workflow?workflow=${encodeURIComponent(workflow)}`,
        {
          workflow,
        }
      ),
    onMutate: async ({ assetId, workflow }) => {
      await queryClient.cancelQueries({
        queryKey: ["asset", assetId],
      });
      const previousAsset = queryClient.getQueryData(["asset", assetId]);
      queryClient.setQueryData(
        ["asset", assetId],
        (previousAsset: AssetAttrsDetails | undefined) => {
          return previousAsset
            ? {
                ...previousAsset,
                processing: { ...previousAsset.processing, workflow },
              }
            : previousAsset;
        }
      );

      return { previousAsset };
    },

    onError: (err, variables, context) => {
      notifications.show(
        "Une erreur est survenue en essayant de changer le status",
        { severity: "error", autoHideDuration: 3000 }
      );
      queryClient.setQueryData(
        ["asset", variables.assetId],
        context?.previousAsset
      );
    },
    onSettled: (data, error, variables) => {
      queryClient.invalidateQueries({ queryKey: ["asset", variables.assetId] });
    },
  });
};

export {
  useAddDocumentsAsset,
  useAsset,
  useAssets,
  useConfig,
  useConfigs,
  useCreateAsset,
  useCreateConfig,
  useDeleteAsset,
  useDeleteConfig,
  useDeleteDocumentsAsset,
  useDocument,
  useDuplicateConfig,
  useExportProcessings,
  useProcessing,
  useTriggerProcessing,
  useUpdateConfig,
  useUpdateReview,
  useUpdateWorkflow,
};
